import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ExternalLink, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import type { Deal } from "@shared/schema";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

interface DealQuickViewDrawerProps {
  dealId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TRANSACTION_STAGE_COLORS: Record<string, string> = {
  scenario: "bg-slate-500",
  deal: "bg-blue-500",
  funded_loan: "bg-green-500",
};

const TRANSACTION_STAGE_LABELS: Record<string, string> = {
  scenario: "Scenario",
  deal: "Deal",
  funded_loan: "Funded Loan",
};

const DEAL_TYPE_LABELS: Record<string, string> = {
  equipment: "Equipment Finance",
  property: "Property Finance",
  working_capital: "Working Capital",
  residential: "Residential",
  business_acquisition: "Business Acquisition",
  smsf_finance: "SMSF Finance",
  development_finance: "Development Finance",
  other: "Other",
};

function formatCurrency(value: string | null) {
  if (!value) return "-";
  const num = parseFloat(value);
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function formatDate(dateString: string | null) {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-AU", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function DetailRow({ label, value, testId }: { label: string; value: React.ReactNode; testId?: string }) {
  return (
    <div className="flex justify-between items-center py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium" data-testid={testId}>{value}</span>
    </div>
  );
}

export function DealQuickViewDrawer({ dealId, open, onOpenChange }: DealQuickViewDrawerProps) {
  const [, navigate] = useLocation();

  const { data: deal, isLoading } = useQuery<Deal>({
    queryKey: ['/api/crm/deals', dealId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/crm/deals/${dealId}`);
      return res.json();
    },
    enabled: !!dealId && open,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="sm:max-w-[480px] overflow-y-auto"
        data-testid="deal-quick-view-drawer"
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : deal ? (
          <>
            <SheetHeader>
              <SheetTitle>{deal.dealName}</SheetTitle>
              <SheetDescription>Quick view of deal details</SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-1">
              <DetailRow
                label="Deal Type"
                value={
                  <Badge variant="outline" className="capitalize" data-testid="qv-deal-type">
                    {DEAL_TYPE_LABELS[deal.dealType] || deal.dealType}
                  </Badge>
                }
              />
              <Separator />
              <DetailRow
                label="Stage"
                value={
                  <Badge
                    className={`${TRANSACTION_STAGE_COLORS[deal.transactionStage]} text-white`}
                    data-testid="qv-transaction-stage"
                  >
                    {TRANSACTION_STAGE_LABELS[deal.transactionStage]}
                  </Badge>
                }
              />
              <Separator />
              <DetailRow
                label="Amount Requested"
                value={formatCurrency(deal.amountRequested)}
                testId="qv-amount-requested"
              />
              <Separator />
              <DetailRow
                label="Amount Approved"
                value={formatCurrency(deal.amountApproved)}
                testId="qv-amount-approved"
              />
              <Separator />
              <DetailRow
                label="Lender"
                value={deal.lenderName || "-"}
                testId="qv-lender"
              />
              <Separator />
              <DetailRow
                label="Settlement Date"
                value={formatDate(deal.expectedSettlementDate)}
                testId="qv-settlement-date"
              />
              <Separator />
              <DetailRow
                label="Probability"
                value={deal.probability != null ? `${deal.probability}%` : "-"}
                testId="qv-probability"
              />
            </div>

            {deal.notes && (
              <div className="mt-4">
                <p className="text-sm text-muted-foreground mb-1">Notes</p>
                <p className="text-sm">{deal.notes}</p>
              </div>
            )}

            <div className="mt-6">
              <Button
                className="w-full"
                onClick={() => {
                  onOpenChange(false);
                  navigate(`/deals/${deal.id}`);
                }}
                data-testid="qv-open-deal-button"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Deal
              </Button>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm text-muted-foreground">Deal not found</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
